/*
 * PointAndFlow Booking Modal
 * Global drawer triggered by any [data-pf-book] element.
 *
 * Phase 1: Mocked slot availability for UX testing.
 * Phase 2: Replace `fetchSlots()` and `submitBooking()` with calls to
 *          the Cloudflare Worker that talks to Google Calendar.
 */

(function () {
  'use strict';

  // ──────────────────────────────────────────────
  // API endpoint — Vercel backend
  // ──────────────────────────────────────────────
  const API_BASE = 'https://pointandflow.vercel.app/api';

  const SELECTOR = '[data-pf-book]';
  const MODAL_ID = 'pfBooking';

  // Practitioner working hours (phase-1 mock).
  // Replace with real availability from worker in phase 2.
  const WORKING_HOURS = {
    1: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '18:00' }], // Mon
    2: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '18:00' }], // Tue
    3: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '18:00' }], // Wed
    4: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '18:00' }], // Thu
    5: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '17:00' }], // Fri
    6: [{ start: '09:00', end: '13:00' }],                                    // Sat
    0: [],                                                                    // Sun closed
  };

  const STATE = {
    service: null,
    isFirstVisit: false,
    currentMonth: null,
    selectedDate: null,
    selectedTime: null,
    step: 1,
  };

  let modalEl = null;
  let lastFocused = null;

  // ──────────────────────────────────────────────
  // Public bootstrap
  // ──────────────────────────────────────────────
  // Cinematic carousel behavior:
  //   * On condition pages (women-s-health-fertility, pain-recovery, etc.):
  //     ALL cinematic carousel buttons are forced to /pages/booking-an-appointment.
  //     These pages use sub-topic carousels that should funnel to booking.
  //   * On any other page (homepage, etc.): buttons whose href points at a
  //     condition page get the data-pf-book attributes stripped so they
  //     navigate to that page instead of opening the booking drawer. This
  //     preserves the homepage "explore condition pages" behavior.
  const BOOKING_PAGE = '/pages/booking-an-appointment';
  const CONDITION_PAGE_PATHS = [
    'women-s-health-fertility',
    'pain-recovery',
    'stress-emotional-wellbeing',
    'children-s-treatments',
    'aesthetic-skin-wellness',
    'acupuncture-therapies',
    'women-s-health',
    'pain-injury-recovery',
    'beauty-skin',
    'mind-balance',
    'energy-vitality',
    'general-wellbeing',
  ];

  function currentPageIsCondition() {
    const path = window.location.pathname;
    return CONDITION_PAGE_PATHS.some(function (frag) {
      return path.indexOf(frag) !== -1;
    });
  }

  function localePrefix() {
    const match = window.location.pathname.match(/^\/[a-z]{2}(?=\/)/);
    return match ? match[0] : '';
  }

  function stripAllBookingAttrs(btn) {
    btn.removeAttribute('data-pf-book');
    btn.removeAttribute('data-pf-title');
    btn.removeAttribute('data-pf-service-id');
    btn.removeAttribute('data-pf-eyebrow');
    btn.removeAttribute('data-pf-description');
    btn.removeAttribute('data-pf-image');
    btn.removeAttribute('data-pf-duration');
    btn.removeAttribute('data-pf-price');
    btn.removeAttribute('data-pf-currency');
    btn.removeAttribute('data-pf-first-visit-duration');
    btn.removeAttribute('data-pf-first-visit-price');
    btn.classList.add('pf-cinematic-btn-link');
  }

  function stripBookingFromConditionLinks() {
    const buttons = document.querySelectorAll('.pf-cinematic-btn');
    const onConditionPage = currentPageIsCondition();
    const bookingHref = localePrefix() + BOOKING_PAGE;

    buttons.forEach(function (btn) {
      if (onConditionPage) {
        // Force every cinematic button on a condition page to the booking page.
        stripAllBookingAttrs(btn);
        btn.setAttribute('href', bookingHref);
        return;
      }
      // Off-condition-page (e.g. homepage): only strip booking attributes
      // for buttons whose href already points at a condition page, so they
      // navigate there instead of opening the booking drawer. Buttons that
      // are real treatment-booking triggers stay untouched.
      const href = btn.getAttribute('href') || '';
      const isCondition = CONDITION_PAGE_PATHS.some(function (frag) {
        return href.indexOf(frag) !== -1;
      });
      if (isCondition) {
        stripAllBookingAttrs(btn);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stripBookingFromConditionLinks);
  } else {
    stripBookingFromConditionLinks();
  }

  // Capture-phase failsafe: matches the same logic as the DOMContentLoaded
  // sweep. On a condition page, ANY cinematic button click stops the
  // booking handler so default navigation (to booking page) wins. On other
  // pages, only intercept clicks where the href already points at a
  // condition page — real booking triggers fall through to openModal().
  document.addEventListener('click', function (e) {
    const link = e.target.closest('.pf-cinematic-btn');
    if (!link) return;
    if (currentPageIsCondition()) {
      e.stopImmediatePropagation();
      return;
    }
    const href = link.getAttribute('href') || '';
    const isCondition = CONDITION_PAGE_PATHS.some(function (frag) {
      return href.indexOf(frag) !== -1;
    });
    if (isCondition) {
      e.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest(SELECTOR);
    if (!trigger) return;
    // Safety nets — three independent reasons to skip the modal:
    //   1) class is explicitly a navigation link (pf-cinematic-btn-link)
    //   2) trigger has data-pf-no-modal attribute (declarative opt-out)
    //   3) trigger lacks data-pf-title (no real treatment data)
    // Any one of these is enough to let default navigation happen.
    if (trigger.classList.contains('pf-cinematic-btn-link')) return;
    if (trigger.hasAttribute('data-pf-no-modal')) return;
    if (!trigger.hasAttribute('data-pf-title')) return;
    e.preventDefault();
    openModal(trigger);
  });

  document.addEventListener('keydown', function (e) {
    if (!modalEl || modalEl.getAttribute('aria-hidden') === 'true') return;
    if (e.key === 'Escape') closeModal();
  });

  // ──────────────────────────────────────────────
  // Treatment picker — shared 4-option dropdown
  // Lives on every modality CTA card AND inside the drawer. We sync
  // all pickers to the same selection and re-fetch slots when the
  // duration changes inside the drawer.
  // ──────────────────────────────────────────────
  const PICKER_OPTION_IDS = ['followup', 'initial', 'extended', 'cosmetic'];

  function readOption(opt) {
    return {
      id: opt.value,
      title: opt.dataset.name,
      duration: parseInt(opt.dataset.duration, 10),
      price: parseFloat(opt.dataset.price),
      currency: opt.dataset.currency || 'CHF',
      covered: opt.dataset.covered === 'true',
    };
  }

  // Map a trigger button's service id/title to one of the four picker
  // options. Treatment grids and modality CTAs send a wider variety of
  // service ids ("acupuncture", "tuina", "cupping", "combined-treatment",
  // etc.) — match each to the nearest picker option so the drawer always
  // opens with a defensible default the customer can change.
  function mapTriggerToOption(trigger) {
    const sid = (trigger.dataset.pfServiceId || '').toLowerCase();
    const title = (trigger.dataset.pfTitle || '').toLowerCase();
    if (PICKER_OPTION_IDS.indexOf(sid) !== -1) return sid;
    const text = sid + ' ' + title;
    if (text.indexOf('cosmetic') !== -1 || text.indexOf('facial') !== -1) return 'cosmetic';
    if (text.indexOf('initial') !== -1 || text.indexOf('first') !== -1) return 'initial';
    if (text.indexOf('extended') !== -1 || text.indexOf('combined') !== -1) return 'extended';
    return 'followup';
  }

  // Read the option's data from the picker (by id). Falls back to the
  // first picker option if no picker exists yet (e.g. an old cached page).
  function readPickerOptionById(optionId) {
    const opt = document.querySelector(
      '[data-pf-trt-select] option[value="' + optionId + '"]'
    );
    if (opt) return readOption(opt);
    return null;
  }

  function updatePickerUI(pickerEl, opt) {
    // Description panes
    pickerEl.querySelectorAll('[data-trt-pane]').forEach(function (pane) {
      pane.hidden = pane.dataset.trtPane !== opt.id;
    });
    // Insurance notes
    pickerEl.querySelectorAll('[data-pf-trt-insurance]').forEach(function (note) {
      const isCoveredNote = note.dataset.pfTrtInsurance === 'covered';
      note.hidden = opt.covered !== isCoveredNote;
    });
    // Duration / price tiles
    const durEl = pickerEl.querySelector('[data-pf-trt-duration]');
    const priceEl = pickerEl.querySelector('[data-pf-trt-price]');
    if (durEl) durEl.textContent = opt.duration + ' min';
    if (priceEl) priceEl.textContent = opt.currency + ' ' + opt.price;
  }

  function syncAllPickersTo(optionId) {
    // Normalize — unknown ids always fall through to followup so the
    // sync never throws on a value the picker doesn't recognize.
    const safeId = PICKER_OPTION_IDS.indexOf(optionId) !== -1 ? optionId : 'followup';

    document.querySelectorAll('[data-pf-trt-picker]').forEach(function (picker) {
      const select = picker.querySelector('[data-pf-trt-select]');
      if (!select) return;
      if (select.value !== safeId) select.value = safeId;
      // After assignment, the selectedIndex should be valid. If for any
      // reason it isn't, bail rather than throwing.
      const opt = select.options[select.selectedIndex];
      if (!opt) return;
      updatePickerUI(picker, readOption(opt));
    });

    // Update CTA buttons on the same page so a subsequent click carries
    // the latest selection. Booking-grid cards keep their own id (they're
    // a catalog, not a picker), so we only touch .pf-tcta-btn here.
    const refSelect = document.querySelector('[data-pf-trt-select]');
    if (!refSelect) return;
    const refOpt = refSelect.options[refSelect.selectedIndex];
    if (!refOpt) return;
    const o = readOption(refOpt);
    document.querySelectorAll('.pf-tcta-btn[data-pf-book]').forEach(function (btn) {
      btn.dataset.pfServiceId = o.id;
      btn.dataset.pfTitle = o.title;
      btn.dataset.pfDuration = String(o.duration);
      btn.dataset.pfPrice = String(o.price);
      btn.dataset.pfCurrency = o.currency;
    });
  }

  // Wire up change listeners on all pickers (idempotent — guarded by dataset flag).
  function initPickers() {
    document.querySelectorAll('[data-pf-trt-picker]').forEach(function (picker) {
      if (picker.dataset.pfTrtBound) return;
      picker.dataset.pfTrtBound = '1';
      const select = picker.querySelector('[data-pf-trt-select]');
      if (!select) return;
      select.addEventListener('change', function () {
        const opt = select.options[select.selectedIndex];
        const o = readOption(opt);
        syncAllPickersTo(o.id);
        // If the modal is open and the change came from the drawer picker,
        // update the booking state + refresh slots for the new duration.
        if (modalEl && modalEl.getAttribute('aria-hidden') === 'false') {
          STATE.service = {
            id: o.id, title: o.title, eyebrow: '', description: '',
            image: STATE.service ? STATE.service.image : '',
            duration: o.duration, price: o.price, currency: o.currency,
            firstVisitDuration: null, firstVisitPrice: null,
          };
          STATE.isFirstVisit = false;
          modalEl.querySelector('.pf-booking-title').textContent = o.title;
          if (STATE.selectedDate) {
            // Re-fetch slots with the new duration; clear the time selection.
            STATE.selectedTime = null;
            renderTimes();
          }
        }
      });
      // Initial paint
      const opt = select.options[select.selectedIndex];
      updatePickerUI(picker, readOption(opt));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPickers);
  } else {
    initPickers();
  }

  // ──────────────────────────────────────────────
  // Open / close
  // ──────────────────────────────────────────────
  function openModal(trigger) {
    modalEl = document.getElementById(MODAL_ID);
    if (!modalEl) return;

    lastFocused = document.activeElement;

    // Wrap state setup in try so any unexpected throw still lets the
    // drawer open — the user clicked Book, they should always see the UI.
    try {
      const baseService = readService(trigger);

      // Map the trigger to one of the four picker options. Booking-grid
      // cards (e.g. "Tuina") map to the closest picker option ("followup");
      // a true picker option flows through unchanged.
      const optionId = mapTriggerToOption(trigger);
      const pickerOpt = readPickerOptionById(optionId);
      if (pickerOpt) {
        // Treatment picker is the source of truth for duration/price/title.
        STATE.service = Object.assign({}, baseService, {
          id: pickerOpt.id,
          title: pickerOpt.title,
          duration: pickerOpt.duration,
          price: pickerOpt.price,
          currency: pickerOpt.currency,
          firstVisitDuration: null,
          firstVisitPrice: null,
        });
      } else {
        STATE.service = baseService;
      }
      STATE.isFirstVisit = false;
      STATE.currentMonth = startOfMonth(new Date());
      STATE.selectedDate = null;
      STATE.selectedTime = null;
      STATE.step = 1;

      syncAllPickersTo(STATE.service.id);
      populateService();
      renderCalendar();
      showStep(1);
    } catch (err) {
      console.error('openModal setup error:', err);
    }

    modalEl.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus close button next tick (after slide-in animation starts)
    requestAnimationFrame(() => {
      const closeBtn = modalEl.querySelector('[data-pf-close]');
      if (closeBtn) closeBtn.focus();
    });
  }

  function closeModal() {
    if (!modalEl) return;
    modalEl.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  // ──────────────────────────────────────────────
  // Service data
  // ──────────────────────────────────────────────
  function readService(trigger) {
    const d = trigger.dataset;
    return {
      id: d.pfServiceId || 'service',
      title: d.pfTitle || 'Treatment',
      eyebrow: d.pfEyebrow || '',
      description: d.pfDescription || '',
      image: d.pfImage || '',
      duration: parseInt(d.pfDuration, 10) || 60,
      price: parseFloat(d.pfPrice) || 0,
      firstVisitDuration: parseInt(d.pfFirstVisitDuration, 10) || null,
      firstVisitPrice: parseFloat(d.pfFirstVisitPrice) || null,
      currency: d.pfCurrency || 'CHF',
    };
  }

  function populateService() {
    const s = STATE.service;
    const q = (sel) => modalEl.querySelector(sel);

    q('.pf-booking-title').textContent = s.title;
    q('.pf-booking-eyebrow').textContent = s.eyebrow;
    q('.pf-booking-eyebrow').hidden = !s.eyebrow;

    const descEl = q('.pf-booking-description');
    if (descEl) {
      descEl.textContent = s.description;
      descEl.hidden = !s.description;
    }

    const img = q('.pf-booking-image');
    if (s.image) {
      img.src = s.image;
      img.alt = s.title;
      img.parentElement.hidden = false;
    } else {
      img.parentElement.hidden = true;
    }

    // Duration / Price tiles
    const dur = q('.pf-booking-duration');
    const price = q('.pf-booking-price');
    if (dur) dur.textContent = s.duration + ' min';
    if (price) price.textContent = formatPrice(s.price, s.currency);

    // Reveal the right bullet-description pane for this treatment.
    modalEl.querySelectorAll('[data-trt-info]').forEach(function (pane) {
      pane.hidden = pane.dataset.trtInfo !== s.id;
    });

    // Insurance: covered for treatments 1-3, uncovered for cosmetic.
    const covered = s.id !== 'cosmetic';
    modalEl.querySelectorAll('[data-pf-bk-insurance]').forEach(function (n) {
      const isCoveredNote = n.dataset.pfBkInsurance === 'covered';
      n.hidden = covered !== isCoveredNote;
    });

    // Old first-visit toggle no longer exists; hide if any cached HTML still has it.
    const ftWrap = q('.pf-booking-firsttime');
    if (ftWrap) ftWrap.hidden = true;
  }

  function formatPrice(value, currency) {
    if (!value) return '—';
    return (currency || 'CHF') + ' ' + value;
  }

  // ──────────────────────────────────────────────
  // Calendar
  // ──────────────────────────────────────────────
  function renderCalendar() {
    const grid = modalEl.querySelector('.pf-cal-grid');
    const label = modalEl.querySelector('.pf-cal-month');
    const monthDate = STATE.currentMonth;

    label.textContent = monthDate.toLocaleDateString('en-GB', {
      month: 'long',
      year: 'numeric',
    });

    grid.innerHTML = '';

    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    // Mon=0..Sun=6 (European week start)
    const leadingBlanks = (firstDay.getDay() + 6) % 7;

    for (let i = 0; i < leadingBlanks; i++) {
      const blank = document.createElement('span');
      blank.className = 'pf-cal-cell pf-cal-blank';
      grid.appendChild(blank);
    }

    const today = startOfDay(new Date());

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pf-cal-cell pf-cal-day';
      btn.textContent = day;

      const isPast = date < today;
      const dayHours = WORKING_HOURS[date.getDay()];
      const isClosed = !dayHours || dayHours.length === 0;

      if (isPast || isClosed) {
        btn.disabled = true;
        btn.classList.add('is-disabled');
      } else {
        btn.addEventListener('click', () => selectDate(date));
      }

      if (sameDay(date, today)) btn.classList.add('is-today');
      if (STATE.selectedDate && sameDay(date, STATE.selectedDate)) btn.classList.add('is-selected');

      grid.appendChild(btn);
    }

    // Hook prev/next once
    if (!grid.dataset.bound) {
      grid.dataset.bound = '1';
      modalEl.querySelector('.pf-cal-prev').addEventListener('click', () => {
        STATE.currentMonth = addMonths(STATE.currentMonth, -1);
        STATE.selectedDate = null;
        STATE.selectedTime = null;
        modalEl.querySelector('.pf-times-wrap').hidden = true;
        renderCalendar();
      });
      modalEl.querySelector('.pf-cal-next').addEventListener('click', () => {
        STATE.currentMonth = addMonths(STATE.currentMonth, 1);
        STATE.selectedDate = null;
        STATE.selectedTime = null;
        modalEl.querySelector('.pf-times-wrap').hidden = true;
        renderCalendar();
      });
    }
  }

  function selectDate(date) {
    STATE.selectedDate = date;
    STATE.selectedTime = null;
    renderCalendar();
    renderTimes();
  }

  // ──────────────────────────────────────────────
  // Time slots
  // ──────────────────────────────────────────────
  async function renderTimes() {
    const wrap = modalEl.querySelector('.pf-times-wrap');
    const grid = modalEl.querySelector('.pf-times');
    const contBtn = modalEl.querySelector('.pf-step-continue');

    wrap.hidden = false;
    grid.innerHTML = '<div class="pf-times-loading">Finding available times…</div>';
    contBtn.disabled = true;

    let slots;
    try {
      slots = await fetchSlots(STATE.selectedDate, getEffectiveDuration());
    } catch (err) {
      console.error('fetchSlots error:', err);
      grid.innerHTML = '<div class="pf-times-empty">Could not load available times. Please try again.</div>';
      return;
    }
    grid.innerHTML = '';

    if (slots.length === 0) {
      grid.innerHTML = '<div class="pf-times-empty">No times available on this day. Please pick another date.</div>';
      return;
    }

    slots.forEach((slot) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pf-time';
      btn.textContent = slot;
      btn.addEventListener('click', () => selectTime(slot));
      if (STATE.selectedTime === slot) btn.classList.add('is-selected');
      grid.appendChild(btn);
    });
  }

  function selectTime(slot) {
    STATE.selectedTime = slot;
    modalEl.querySelectorAll('.pf-time').forEach((b) => {
      b.classList.toggle('is-selected', b.textContent === slot);
    });
    modalEl.querySelector('.pf-step-continue').disabled = false;
  }

  function getEffectiveDuration() {
    // first-visit toggle is obsolete — the picker handles all variants now.
    return STATE.service.duration;
  }

  // ──────────────────────────────────────────────
  // Real fetch — Vercel + Google Calendar
  // ──────────────────────────────────────────────
  async function fetchSlots(date, durationMin) {
    const dateStr = fmtLocalDate(date);
    const res = await fetch(`${API_BASE}/slots?date=${dateStr}&duration=${durationMin}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json()).error || ''; } catch {}
      throw new Error(detail || `Slots failed (${res.status})`);
    }
    const data = await res.json();
    return data.slots || [];
  }

  // ──────────────────────────────────────────────
  // Stepper
  // ──────────────────────────────────────────────
  function showStep(n) {
    STATE.step = n;
    modalEl.querySelectorAll('[data-pf-step]').forEach((el) => {
      el.hidden = parseInt(el.dataset.pfStep, 10) !== n;
    });
    // Reset scroll inside drawer
    modalEl.querySelector('.pf-booking-scroll').scrollTop = 0;
  }

  // ──────────────────────────────────────────────
  // Form submit — POST to /api/book
  // ──────────────────────────────────────────────
  async function submitBooking(form) {
    const data = new FormData(form);
    const payload = {
      serviceId: STATE.service.id,
      serviceTitle: STATE.service.title,
      date: fmtLocalDate(STATE.selectedDate),
      time: STATE.selectedTime,
      duration: getEffectiveDuration(),
      isFirstVisit: STATE.isFirstVisit,
      name:  (data.get('name')  || '').toString().trim(),
      email: (data.get('email') || '').toString().trim(),
      phone: (data.get('phone') || '').toString().trim(),
      notes: (data.get('notes') || '').toString().trim(),
    };

    try {
      const res = await fetch(`${API_BASE}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        return { ok: false, error: json.error || `Booking failed (${res.status})`, payload };
      }
      return { ok: true, eventId: json.eventId, htmlLink: json.htmlLink, payload };
    } catch (err) {
      return { ok: false, error: err.message || 'Network error', payload };
    }
  }

  // ──────────────────────────────────────────────
  // Wire up controls once DOM ready
  // ──────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    modalEl = modal;

    // Close buttons / scrim
    modal.querySelectorAll('[data-pf-close]').forEach((el) => {
      el.addEventListener('click', closeModal);
    });

    // First-visit toggle is obsolete (replaced by the treatment picker dropdown).

    // Continue from step 1 → step 2
    const cont = modal.querySelector('.pf-step-continue');
    if (cont) {
      cont.addEventListener('click', () => {
        if (!STATE.selectedDate || !STATE.selectedTime) return;
        showStep(2);
      });
    }

    // Back on step 2
    const back = modal.querySelector('.pf-step-back');
    if (back) {
      back.addEventListener('click', () => showStep(1));
    }

    // Form submit
    const form = modal.querySelector('.pf-booking-form');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('button[type=submit]');

        // Clear any previous error
        const oldErr = form.querySelector('.pf-form-error');
        if (oldErr) oldErr.remove();

        btn.disabled = true;
        btn.dataset.label = btn.textContent;
        btn.textContent = 'Sending…';

        const result = await submitBooking(form);

        btn.disabled = false;
        btn.textContent = btn.dataset.label;

        if (result.ok) {
          const s = STATE.service;
          const dateLabel = STATE.selectedDate.toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
          });
          modal.querySelector('.pf-success-service').textContent = s.title;
          modal.querySelector('.pf-success-when').textContent = `${dateLabel} at ${STATE.selectedTime}`;
          modal.querySelector('.pf-success-duration').textContent = `${getEffectiveDuration()} min`;
          showStep(3);
        } else {
          const err = document.createElement('div');
          err.className = 'pf-form-error';
          err.textContent = result.error || 'Something went wrong. Please try again.';
          form.insertBefore(err, btn);
        }
      });
    }
  });

  // ──────────────────────────────────────────────
  // Utils
  // ──────────────────────────────────────────────
  function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
  function startOfDay(d)   { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
  function sameDay(a, b)   {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
  }
  function toMinutes(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  }
  function fromMinutes(min) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  function fmtLocalDate(date) {
    // Build YYYY-MM-DD from the date's LOCAL components (avoids the UTC shift
    // that toISOString() introduces near midnight in non-UTC timezones).
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
})();
